import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { updateUserProfile, isUsernameAvailable } from '../services/userService';
import { validateInput, rateLimiter } from '../utils/security';
import { db, storage } from '../services/firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getCommentsForQuestion } from '../services/questionsService';
import { useAuth } from '../context/AuthContext';
import Comment from '../components/Comment';
import StreakDisplay from '../components/StreakDisplay';
import BadgeModal from '../components/BadgeModal';
import ProfileRing from '../components/ProfileRing';
import RingSelectionModal from '../components/RingSelectionModal';
import { followUser, unfollowUser, checkIfFollowing, getFollowerCount, getFollowingCount } from '../services/followService';
import { 
  createClapNotification, 
  createTomatoNotification, 
  createFollowNotification,
  createBadgeNotification,
  createMilestoneNotification 
} from '../services/notificationService';
import { 
  updateUserStreak, 
  getUserStreak, 
  getStreakStats,
  STREAK_ACTIVITIES 
} from '../services/streakService';
import { 
  getUserBadges, 
  checkForNewBadges, 
  trackBadgeActivity,
  getUserBadgeProgress,
  getBadgesByCategory,
  getBadgeStats,
  ALL_BADGES
} from '../services/badgeService';
import {
  getUserActiveRing,
  checkForNewMilestoneRings
} from '../services/profileRingService';

// Badge definitions (now using comprehensive badge service)
const BADGES = ALL_BADGES;

function Profile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userComments, setUserComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [isFollowing, setIsFollowing] = useState(false);
  const [userStats, setUserStats] = useState({
    debates: 0,
    clapsReceived: 0,
    tomatoesReceived: 0,
    followers: 0,
    following: 0,
    streak: 0,
    hotTakes: 0
  });
  const [userBadges, setUserBadges] = useState([]);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [userHotTakes, setUserHotTakes] = useState([]);
  const [showCreateHotTake, setShowCreateHotTake] = useState(false);
  const [newHotTake, setNewHotTake] = useState({
    question: '',
    optionA: '',
    optionB: ''
  });
  const [hotTakeComments, setHotTakeComments] = useState({});
  const [showCommentsFor, setShowCommentsFor] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [streakStats, setStreakStats] = useState(null);
  const [badgeProgress, setBadgeProgress] = useState(null);
  const [badgeStats, setBadgeStats] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [activeRingId, setActiveRingId] = useState(null);
  const [showRingModal, setShowRingModal] = useState(false);
  
  // Form state for editing profile
  const [formData, setFormData] = useState({
    displayName: '',
    username: '',
    bio: ''
  });
  
  // Helper function to get comment border color
  const getCommentBorderColor = (userOption) => {
    return userOption === 'A' ? 'white' : 'black';
  };
  
  // Helper function to update user streak (for app visits only)
  const updateStreakForVisit = async () => {
    if (!currentUser) return;
    
    try {
      await updateUserStreak(currentUser.uid);
      // Refresh streak stats
      const newStreakStats = await getStreakStats(currentUser.uid);
      setStreakStats(newStreakStats);
    } catch (error) {
      console.error('Failed to update streak:', error);
    }
  };
  
  // Calculate user stats from various sources
  const calculateUserStats = async (uid) => {
    try {
      const stats = {
        debates: 0,
        clapsReceived: 0,
        tomatoesReceived: 0,
        followers: 0,
        following: 0,
        streak: 0,
        hotTakes: 0,
        userId: parseInt(uid.replace(/[^0-9]/g, '').slice(-3)) || 999 // Extract number from UID for early bird
      };
      
      // Get votes count (debates)
      const votedKey = currentUser ? `votedQuestions_${uid}` : 'votedQuestions_anonymous';
      const votedQuestions = JSON.parse(localStorage.getItem(votedKey) || '{}');
      stats.debates = Object.keys(votedQuestions).length;
      
      // Get streak from streak service if available
      let currentStreak = 0;
      if (streakStats && streakStats.currentStreak) {
        currentStreak = streakStats.currentStreak;
      } else {
        // Fallback: Calculate streak from vote timestamps
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        let lastVoteTime = null;
        
        const voteTimestamps = Object.values(votedQuestions)
          .map(v => v.timestamp)
          .filter(t => t)
          .sort((a, b) => b - a);
        
        for (const timestamp of voteTimestamps) {
          if (!lastVoteTime) {
            if (now - timestamp < oneDayMs) {
              currentStreak = 1;
              lastVoteTime = timestamp;
            } else {
              break;
            }
          } else {
            const dayDiff = Math.floor((lastVoteTime - timestamp) / oneDayMs);
            if (dayDiff <= 1) {
              currentStreak++;
              lastVoteTime = timestamp;
            } else {
              break;
            }
          }
        }
      }
      stats.streak = currentStreak;
      
      // Get claps and tomatoes from localStorage interaction data
      const interactions = JSON.parse(localStorage.getItem(`interactions_${uid}`) || '{}');
      stats.clapsReceived = interactions.clapsReceived || 0;
      stats.tomatoesReceived = interactions.tomatoesReceived || 0;
      
      // Get follower/following counts from follow service
      try {
        const [followerCount, followingCount] = await Promise.all([
          getFollowerCount(uid),
          getFollowingCount(uid)
        ]);
        stats.followers = followerCount;
        stats.following = followingCount;
      } catch (err) {
        console.error('Error fetching follow data:', err);
      }
      
      // Calculate hot takes (simplified - would need real data)
      stats.hotTakes = Math.floor(stats.debates / 5);
      
      return stats;
    } catch (err) {
      console.error('Error calculating user stats:', err);
      return {
        debates: 0,
        clapsReceived: 0,
        tomatoesReceived: 0,
        followers: 0,
        following: 0,
        streak: 0,
        hotTakes: 0
      };
    }
  };
  
  // Check which badges the user has earned (using new comprehensive system)
  const checkUserBadges = async (stats, progress) => {
    try {
      // Check for newly earned badges
      const newBadges = await checkForNewBadges(userId, stats, progress);
      
      // Get all earned badges
      const earnedBadges = await getUserBadges(userId);
      
      // Update badge stats
      const badgeStatsData = await getBadgeStats(userId);
      setBadgeStats(badgeStatsData);
      
      return earnedBadges.slice(0, 6); // Show max 6 badges in profile header
    } catch (err) {
      console.error('Error checking user badges:', err);
      return [];
    }
  };
  
  // Check if current user is following this profile
  const checkFollowStatus = async () => {
    if (!currentUser || currentUser.uid === userId) return;
    
    try {
      const following = await checkIfFollowing(currentUser.uid, userId);
      setIsFollowing(following);
    } catch (err) {
      console.error('Error checking follow status:', err);
    }
  };
  
  // Handle follow/unfollow
  const handleFollowToggle = async () => {
    if (!currentUser) {
      // Show login modal or redirect
      return;
    }
    
    try {
      if (isFollowing) {
        // Unfollow
        await unfollowUser(currentUser.uid, userId);
        setIsFollowing(false);
        setUserStats(prev => ({ ...prev, followers: prev.followers - 1 }));
      } else {
        // Follow
        await followUser(currentUser.uid, userId);
        setIsFollowing(true);
        setUserStats(prev => ({ ...prev, followers: prev.followers + 1 }));
        
        // Create follow notification
        try {
          await createFollowNotification(userId, {
            uid: currentUser.uid,
            displayName: currentUser.displayName,
            username: currentUser.username || currentUser.displayName || 'user'
          });
        } catch (notifErr) {
          console.error('Failed to create follow notification:', notifErr);
        }
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    }
  };
  
  // Load user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        const userDocRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setProfile({
            id: userId,
            ...userData
          });
          
          setFormData({
            displayName: userData.displayName || '',
            username: userData.username || '',
            bio: userData.bio || ''
          });
        } else if (currentUser && currentUser.uid === userId) {
          setProfile({
            id: currentUser.uid,
            displayName: currentUser.displayName || '',
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            isNewProfile: true
          });
          
          setFormData({
            displayName: currentUser.displayName || '',
            username: '',
            bio: ''
          });
        } else {
          setError('User not found');
        }
        
        // Calculate stats
        const stats = await calculateUserStats(userId);
        setUserStats(stats);
        
        // Load badge progress
        const progress = await getUserBadgeProgress(userId);
        setBadgeProgress(progress);
        
        // Check badges
        const badges = await checkUserBadges(stats, progress);
        setUserBadges(badges);
        
        // Load active ring
        const activeRing = await getUserActiveRing(userId);
        setActiveRingId(activeRing);
        
        // Check for new milestone rings
        await checkForNewMilestoneRings(userId, stats);
        
        // Check follow status
        await checkFollowStatus();
        
        // Load user's hot takes
        await loadUserHotTakes();
        
        // Load streak stats
        if (currentUser && currentUser.uid === userId) {
          try {
            const userStreakStats = await getStreakStats(userId);
            setStreakStats(userStreakStats);
          } catch (streakErr) {
            console.error('Error loading streak stats:', streakErr);
          }
        }
        
      } catch (err) {
        console.error('Error fetching user profile:', err);
        if (currentUser && currentUser.uid === userId) {
          setProfile({
            id: currentUser.uid,
            displayName: currentUser.displayName || '',
            email: currentUser.email,
            photoURL: currentUser.photoURL,
            isNewProfile: true
          });
          
          setFormData({
            displayName: currentUser.displayName || '',
            username: '',
            bio: ''
          });
        } else {
          setError('Failed to load user profile');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [userId, currentUser]);
  
  // Load user's comments
  useEffect(() => {
    const fetchUserComments = async () => {
      setLoadingComments(true);
      try {
        const allCommentsData = await getCommentsForQuestion('sample-question', 1, 100);
        
        if (allCommentsData && allCommentsData.comments) {
          const userComments = allCommentsData.comments.filter(
            comment => comment.userId === userId
          );
          setUserComments(userComments.slice(0, 10)); // Show latest 10
        }
      } catch (err) {
        console.error('Error fetching user comments:', err);
      } finally {
        setLoadingComments(false);
      }
    };
    
    fetchUserComments();
  }, [userId]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (name === 'username') {
      setUsernameError('');
    }
  };
  
  // Handle profile photo change
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please select an image smaller than 5MB');
      return;
    }

    setUploadingPhoto(true);

    try {
      // Upload to Firebase Storage
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `user_photos/${userId}/${fileName}`);

      // Upload the file
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Photo uploaded successfully:', uploadResult);

      // Get the download URL
      const photoURL = await getDownloadURL(uploadResult.ref);
      console.log('Photo URL:', photoURL);

      // Update profile with new photo URL
      await updateUserProfile(userId, {
        photoURL: photoURL
      });

      // Update local state
      setProfile({
        ...profile,
        photoURL: photoURL
      });

      setUploadingPhoto(false);
      alert('Profile photo updated successfully!');
    } catch (err) {
      console.error('Error uploading photo:', err);
      alert('Failed to upload photo: ' + err.message);
      setUploadingPhoto(false);
    }
  };
  
  // Load user's hot takes
  const loadUserHotTakes = async () => {
    try {
      console.log('Loading hot takes for user:', userId);
      const hotTakesQuery = query(
        collection(db, 'userHotTakes'),
        where('authorId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      const hotTakesSnapshot = await getDocs(hotTakesQuery);
      const hotTakes = hotTakesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      console.log('Loaded hot takes:', hotTakes);
      setUserHotTakes(hotTakes);
      
      // Load comments for each hot take
      for (const hotTake of hotTakes) {
        await loadHotTakeComments(hotTake.id);
      }
    } catch (err) {
      console.error('Error loading hot takes:', err);
      // Fallback: try without orderBy in case index is missing
      try {
        console.log('Retrying without orderBy...');
        const fallbackQuery = query(
          collection(db, 'userHotTakes'),
          where('authorId', '==', userId),
          limit(10)
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        const fallbackHotTakes = fallbackSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        console.log('Fallback hot takes loaded:', fallbackHotTakes);
        setUserHotTakes(fallbackHotTakes);
      } catch (fallbackErr) {
        console.error('Fallback query also failed:', fallbackErr);
      }
    }
  };
  
  // Load comments for a specific hot take
  const loadHotTakeComments = async (hotTakeId) => {
    try {
      const commentsQuery = query(
        collection(db, 'hotTakeComments'),
        where('hotTakeId', '==', hotTakeId),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const commentsSnapshot = await getDocs(commentsQuery);
      const comments = commentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setHotTakeComments(prev => ({
        ...prev,
        [hotTakeId]: comments
      }));
    } catch (err) {
      console.error('Error loading hot take comments:', err);
    }
  };
  
  // Handle creating a new hot take
  const handleCreateHotTake = async (e) => {
    e.preventDefault();
    
    if (!newHotTake.question.trim() || !newHotTake.optionA.trim() || !newHotTake.optionB.trim()) {
      alert('Please fill in all fields');
      return;
    }
    
    if (!currentUser || !userId) {
      alert('You must be logged in to create hot takes');
      return;
    }
    
    try {
      console.log('Creating hot take for user:', userId);
      const hotTakeData = {
        authorId: userId,
        authorName: profile.displayName || profile.username || 'User',
        question: newHotTake.question.trim(),
        optionA: newHotTake.optionA.trim(),
        optionB: newHotTake.optionB.trim(),
        optionAVotes: 0,
        optionBVotes: 0,
        totalVotes: 0,
        claps: 0,
        tomatoes: 0,
        commentCount: 0,
        createdAt: Date.now()
      };
      
      console.log('Hot take data:', hotTakeData);
      const docRef = await addDoc(collection(db, 'userHotTakes'), hotTakeData);
      console.log('Hot take created with ID:', docRef.id);
      
      // Add to local state
      const newHotTake = {
        id: docRef.id,
        ...hotTakeData
      };
      setUserHotTakes(prev => [newHotTake, ...prev]);
      console.log('Added to local state:', newHotTake);
      
      // Track badge progress for hot take creation
      try {
        await trackBadgeActivity(userId, 'hot_take_created');
      } catch (badgeErr) {
        console.error('Error tracking badge activity:', badgeErr);
      }
      
      // Reset form
      setNewHotTake({ question: '', optionA: '', optionB: '' });
      setShowCreateHotTake(false);
      
      alert('Hot take created successfully!');
      
    } catch (err) {
      console.error('Error creating hot take:', err);
      alert('Failed to create hot take: ' + err.message);
    }
  };
  
  // Handle voting on a hot take
  const handleHotTakeVote = async (hotTakeId, option) => {
    try {
      const hotTakeRef = doc(db, 'userHotTakes', hotTakeId);
      const voteKey = `hotTakeVotes_${currentUser?.uid || 'anonymous'}`;
      const userVotes = JSON.parse(localStorage.getItem(voteKey) || '{}');
      
      // Check if user already voted
      if (userVotes[hotTakeId]) {
        alert('You have already voted on this hot take');
        return;
      }
      
      // Update vote counts
      const updateData = {};
      if (option === 'A') {
        updateData.optionAVotes = userHotTakes.find(ht => ht.id === hotTakeId).optionAVotes + 1;
      } else {
        updateData.optionBVotes = userHotTakes.find(ht => ht.id === hotTakeId).optionBVotes + 1;
      }
      updateData.totalVotes = userHotTakes.find(ht => ht.id === hotTakeId).totalVotes + 1;
      
      await updateDoc(hotTakeRef, updateData);
      
      // Update local state
      setUserHotTakes(prev => prev.map(ht => 
        ht.id === hotTakeId 
          ? { ...ht, ...updateData }
          : ht
      ));
      
      // Store vote in localStorage
      userVotes[hotTakeId] = option;
      localStorage.setItem(voteKey, JSON.stringify(userVotes));
      
      
    } catch (err) {
      console.error('Error voting on hot take:', err);
      alert('Failed to vote');
    }
  };
  
  // Handle deleting a hot take
  const handleDeleteHotTake = async (hotTakeId) => {
    if (!window.confirm('Are you sure you want to delete this hot take?')) return;
    
    try {
      await deleteDoc(doc(db, 'userHotTakes', hotTakeId));
      setUserHotTakes(prev => prev.filter(ht => ht.id !== hotTakeId));
    } catch (err) {
      console.error('Error deleting hot take:', err);
      alert('Failed to delete hot take');
    }
  };
  
  // Handle clapping a hot take
  const handleClapHotTake = async (hotTakeId) => {
    if (!currentUser) {
      window.alert('Please log in to clap');
      return;
    }
    
    try {
      const clapKey = `hotTakeClaps_${currentUser.uid}`;
      const userClaps = JSON.parse(localStorage.getItem(clapKey) || '{}');
      
      if (userClaps[hotTakeId]) {
        window.alert('You have already clapped this hot take');
        return;
      }
      
      const hotTakeRef = doc(db, 'userHotTakes', hotTakeId);
      const currentHotTake = userHotTakes.find(ht => ht.id === hotTakeId);
      const newClapCount = (currentHotTake.claps || 0) + 1;
      
      await updateDoc(hotTakeRef, { claps: newClapCount });
      
      // Update local state
      setUserHotTakes(prev => prev.map(ht => 
        ht.id === hotTakeId 
          ? { ...ht, claps: newClapCount }
          : ht
      ));
      
      // Store clap in localStorage
      userClaps[hotTakeId] = true;
      localStorage.setItem(clapKey, JSON.stringify(userClaps));
      
      // Create clap notification (only if not clapping own hot take)
      if (currentHotTake.authorId !== currentUser.uid) {
        try {
          await createClapNotification(
            currentHotTake.authorId,
            {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              username: currentUser.username || currentUser.displayName || 'user'
            },
            'hot take',
            hotTakeId,
            currentHotTake.question
          );
        } catch (notifErr) {
          console.error('Failed to create clap notification:', notifErr);
        }
      }
      
      // Track badge progress for voting activity
      if (currentUser) {
        await trackBadgeActivity(currentUser.uid, 'claps_received', { 
          count: newClapCount 
        });
      }
      
    } catch (err) {
      console.error('Error clapping hot take:', err);
      window.alert('Failed to clap');
    }
  };
  
  // Handle throwing tomato at hot take
  const handleTomatoHotTake = async (hotTakeId) => {
    if (!currentUser) {
      window.alert('Please log in to throw tomatoes');
      return;
    }
    
    try {
      const tomatoKey = `hotTakeTomatoes_${currentUser.uid}`;
      const userTomatoes = JSON.parse(localStorage.getItem(tomatoKey) || '{}');
      
      if (userTomatoes[hotTakeId]) {
        window.alert('You have already thrown a tomato at this hot take');
        return;
      }
      
      const hotTakeRef = doc(db, 'userHotTakes', hotTakeId);
      const currentHotTake = userHotTakes.find(ht => ht.id === hotTakeId);
      const newTomatoCount = (currentHotTake.tomatoes || 0) + 1;
      
      await updateDoc(hotTakeRef, { tomatoes: newTomatoCount });
      
      // Update local state
      setUserHotTakes(prev => prev.map(ht => 
        ht.id === hotTakeId 
          ? { ...ht, tomatoes: newTomatoCount }
          : ht
      ));
      
      // Store tomato in localStorage
      userTomatoes[hotTakeId] = true;
      localStorage.setItem(tomatoKey, JSON.stringify(userTomatoes));
      
      // Create tomato notification (only if not throwing at own hot take)
      if (currentHotTake.authorId !== currentUser.uid) {
        try {
          await createTomatoNotification(
            currentHotTake.authorId,
            {
              uid: currentUser.uid,
              displayName: currentUser.displayName,
              username: currentUser.username || currentUser.displayName || 'user'
            },
            'hot take',
            hotTakeId,
            currentHotTake.question
          );
        } catch (notifErr) {
          console.error('Failed to create tomato notification:', notifErr);
        }
      }
      
      // Track badge progress for tomato activity
      if (currentUser) {
        await trackBadgeActivity(currentUser.uid, 'tomatoes_received', { 
          count: newTomatoCount 
        });
      }
      
    } catch (err) {
      console.error('Error throwing tomato at hot take:', err);
      window.alert('Failed to throw tomato');
    }
  };
  
  // Handle adding comment to hot take
  const handleAddComment = async (hotTakeId) => {
    if (!currentUser) {
      window.alert('Please log in to comment');
      return;
    }
    
    if (!newComment.trim()) {
      window.alert('Please enter a comment');
      return;
    }
    
    try {
      const commentData = {
        hotTakeId,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'User',
        text: newComment.trim(),
        createdAt: Date.now()
      };
      
      const docRef = await addDoc(collection(db, 'hotTakeComments'), commentData);
      
      // Update comment count
      const hotTakeRef = doc(db, 'userHotTakes', hotTakeId);
      const currentHotTake = userHotTakes.find(ht => ht.id === hotTakeId);
      const newCommentCount = (currentHotTake.commentCount || 0) + 1;
      
      await updateDoc(hotTakeRef, { commentCount: newCommentCount });
      
      // Update local states
      setHotTakeComments(prev => ({
        ...prev,
        [hotTakeId]: [{
          id: docRef.id,
          ...commentData
        }, ...(prev[hotTakeId] || [])]
      }));
      
      setUserHotTakes(prev => prev.map(ht => 
        ht.id === hotTakeId 
          ? { ...ht, commentCount: newCommentCount }
          : ht
      ));
      
      setNewComment('');
      
      // Track badge progress for commenting
      if (currentUser) {
        await trackBadgeActivity(currentUser.uid, 'comment_posted');
      }
      
    } catch (err) {
      console.error('Error adding comment:', err);
      window.alert('Failed to add comment');
    }
  };
  
  // Toggle comments visibility
  const toggleComments = async (hotTakeId) => {
    if (showCommentsFor === hotTakeId) {
      setShowCommentsFor(null);
    } else {
      setShowCommentsFor(hotTakeId);
      // Load comments if not already loaded
      if (!hotTakeComments[hotTakeId]) {
        await loadHotTakeComments(hotTakeId);
      }
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Rate limiting check
    const rateCheck = rateLimiter.isRateLimited('profile_update', currentUser?.uid || 'anonymous', 3, 300000); // 3 updates per 5 minutes
    if (rateCheck.limited) {
      setError('Too many profile updates. Please wait before updating again.');
      return;
    }
    
    // Validate display name
    const displayNameValidation = validateInput.displayName(formData.displayName);
    if (!displayNameValidation.valid) {
      setError(displayNameValidation.error);
      return;
    }
    
    // Validate username if provided
    let usernameValidation = null;
    if (formData.username) {
      usernameValidation = validateInput.username(formData.username);
      if (!usernameValidation.valid) {
        setUsernameError(usernameValidation.error);
        return;
      }
      
      if (formData.username !== profile.username) {
        const isAvailable = await isUsernameAvailable(usernameValidation.value);
        if (!isAvailable) {
          setUsernameError('This username is already taken');
          return;
        }
      }
    }
    
    // Validate bio
    const bioValidation = validateInput.bio(formData.bio);
    if (!bioValidation.valid) {
      setError(bioValidation.error);
      return;
    }
    
    try {
      await updateUserProfile(userId, {
        displayName: displayNameValidation.value,
        username: usernameValidation ? usernameValidation.value : formData.username,
        bio: bioValidation.value
      });
      
      // Clear cached user data to force refresh across the app
      const userProfiles = JSON.parse(localStorage.getItem('userProfiles') || '{}');
      if (userProfiles[userId]) {
        userProfiles[userId] = {
          ...userProfiles[userId],
          displayName: formData.displayName,
          username: formData.username,
          bio: formData.bio,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem('userProfiles', JSON.stringify(userProfiles));
      }
      
      // Also clear any display name cache that might exist
      const cacheKeys = Object.keys(localStorage).filter(key => 
        key.includes('displayNameCache') || 
        key.includes('userCache') || 
        key.includes('profileCache')
      );
      cacheKeys.forEach(key => localStorage.removeItem(key));
      
      setProfile({
        ...profile,
        displayName: formData.displayName,
        username: formData.username,
        bio: formData.bio
      });
      
      setIsEditing(false);
      
      // Force page reload to ensure all components show updated data
      window.location.reload();
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile');
    }
  };
  
  if (loading) {
    return (
      <div className="profile-loading">
        <div className="emoji-loader">
          <span className="emoji-item">👤</span>
          <span className="emoji-item">📊</span>
          <span className="emoji-item">🏅</span>
          <span className="emoji-item">🔥</span>
        </div>
        <p>Loading profile...</p>
      </div>
    );
  }
  
  if (error) {
    return <div className="profile-error">{error}</div>;
  }
  
  if (!profile) {
    return <div className="profile-not-found">User not found</div>;
  }
  
  return (
    <div className="profile-container">
      {/* Back Button */}
      <button 
        className="profile-back-button"
        onClick={() => navigate('/')}
        aria-label="Back to For You page"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          width="20" 
          height="20"
        >
          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.42-1.41L7.83 13H20v-2z"/>
        </svg>
      </button>

      {/* Header Section */}
      <div className="profile-header-section">
        <div className="profile-header-content">
          <div className="profile-avatar-container">
            <ProfileRing ringId={activeRingId} size="xlarge">
              <div className="profile-avatar-large">
                {profile.photoURL ? (
                  <img src={profile.photoURL} alt={profile.displayName || 'User'} />
                ) : (
                  <div className="profile-avatar-placeholder">
                    {(profile.displayName || profile.username || 'A')[0].toUpperCase()}
                  </div>
                )}
              </div>
            </ProfileRing>
            {currentUser && currentUser.uid === userId && (
              <>
                <input
                  type="file"
                  id="photo-upload"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  style={{ display: 'none' }}
                />
                <label htmlFor="photo-upload" className="profile-photo-upload-button">
                  {uploadingPhoto ? '...' : '+'}
                </label>
              </>
            )}
          </div>
          
          <div className="profile-identity">
            <h1 className="profile-display-name">{profile.displayName || 'Anonymous Debater'}</h1>
            {profile.username && (
              <div className="profile-username">@{profile.username}</div>
            )}
            {profile.bio && (
              <div className="profile-bio-text">{profile.bio}</div>
            )}
          </div>
          
          <div className="profile-actions-section">
            {currentUser && currentUser.uid === userId ? (
              <>
                <button onClick={() => setIsEditing(true)} className="profile-action-button edit">
                  ✏️ Edit Profile
                </button>
                <button onClick={() => setShowRingModal(true)} className="profile-action-button ring">
                  💍 Change Ring
                </button>
              </>
            ) : (
              <button 
                onClick={handleFollowToggle} 
                className={`profile-action-button follow ${isFollowing ? 'following' : ''}`}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Impact Row - Stats */}
      <div className="profile-impact-row">
        <div className="impact-stat">
          <div className="stat-number">{userStats.debates}</div>
          <div className="stat-label">Votes</div>
        </div>
        <div className="impact-stat">
          <div className="stat-number">👏 {userStats.clapsReceived}</div>
          <div className="stat-label">Claps</div>
        </div>
        <div className="impact-stat">
          <div className="stat-number">🍅 {userStats.tomatoesReceived}</div>
          <div className="stat-label">Tomatoes</div>
        </div>
        <div className="impact-stat">
          <div className="stat-number">{userStats.followers}</div>
          <div className="stat-label">Followers</div>
        </div>
        <div className="impact-stat">
          <div className="stat-number">{userStats.following}</div>
          <div className="stat-label">Following</div>
        </div>
      </div>
      
      {/* Streak Display */}
      {streakStats && currentUser && currentUser.uid === userId && (
        <div className="profile-streak-section">
          <StreakDisplay streakStats={streakStats} showDetails={true} />
        </div>
      )}
      
      {/* Badges Strip */}
      <div className="profile-badges-section">
        <div className="badges-header">
          <h3 className="badges-title">
            Achievements 
            {badgeStats && (
              <span className="badge-count">({badgeStats.earnedCount}/{badgeStats.totalBadges})</span>
            )}
          </h3>
          <button 
            className="view-all-badges-button"
            onClick={() => setShowBadgeModal(true)}
          >
            View All
          </button>
        </div>
        
        {userBadges.length > 0 ? (
          <div className="badges-strip">
            {userBadges.map((badge) => {
              const badgeData = ALL_BADGES[badge.id];
              const rarity = badgeData ? badgeData.rarity : 'common';
              return (
                <div 
                  key={badge.id} 
                  className={`badge-item rarity-${rarity}`}
                  onClick={() => setSelectedBadge(badge)}
                  title={badgeData ? badgeData.description : badge.description}
                >
                  <span className="badge-icon">{badge.icon}</span>
                  <span className="badge-name">{badge.name}</span>
                  {rarity === 'legendary' && <div className="legendary-glow"></div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-badges-yet">
            <div className="no-badges-icon">🎯</div>
            <div className="no-badges-text">
              Start earning badges by engaging in debates!
            </div>
          </div>
        )}
      </div>
      
      {/* Badge Detail Modal */}
      {selectedBadge && (
        <div className="badge-modal" onClick={() => setSelectedBadge(null)}>
          <div className="badge-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="badge-modal-icon">{selectedBadge.icon}</div>
            <h3>{selectedBadge.name}</h3>
            <p>{selectedBadge.description}</p>
            <button onClick={() => setSelectedBadge(null)}>Close</button>
          </div>
        </div>
      )}
      
      {/* Hot Takes Section */}
      <div className="profile-activity-section">
        <div className="activity-header">
          <h2 className="activity-title">Hot Takes</h2>
          {currentUser && currentUser.uid === userId && (
            <button 
              onClick={() => setShowCreateHotTake(true)} 
              className="create-hot-take-button"
            >
              + Create Hot Take
            </button>
          )}
        </div>
        
        {userHotTakes.length > 0 ? (
          <div className="hot-takes-grid">
            {userHotTakes.map(hotTake => (
              <div key={hotTake.id} className="hot-take-card">
                <div className="hot-take-header">
                  <h3 className="hot-take-question">{hotTake.question}</h3>
                  {currentUser && currentUser.uid === userId && (
                    <button 
                      onClick={() => handleDeleteHotTake(hotTake.id)}
                      className="delete-hot-take-button"
                    >
                      ×
                    </button>
                  )}
                </div>
                
                <div className="hot-take-options">
                  <button 
                    onClick={() => handleHotTakeVote(hotTake.id, 'A')}
                    className="hot-take-option option-a"
                    disabled={!currentUser}
                  >
                    <span className="option-text">{hotTake.optionA}</span>
                    <span className="option-votes">{hotTake.optionAVotes || 0} votes</span>
                  </button>
                  
                  <button 
                    onClick={() => handleHotTakeVote(hotTake.id, 'B')}
                    className="hot-take-option option-b"
                    disabled={!currentUser}
                  >
                    <span className="option-text">{hotTake.optionB}</span>
                    <span className="option-votes">{hotTake.optionBVotes || 0} votes</span>
                  </button>
                </div>
                
                <div className="hot-take-interactions">
                  <button 
                    onClick={() => handleClapHotTake(hotTake.id)}
                    className="hot-take-interaction-button clap"
                    disabled={!currentUser}
                  >
                    👏 {hotTake.claps || 0}
                  </button>
                  
                  <button 
                    onClick={() => handleTomatoHotTake(hotTake.id)}
                    className="hot-take-interaction-button tomato"
                    disabled={!currentUser}
                  >
                    🍅 {hotTake.tomatoes || 0}
                  </button>
                  
                  <button 
                    onClick={() => toggleComments(hotTake.id)}
                    className="hot-take-interaction-button comments"
                  >
                    💬 {hotTake.commentCount || 0}
                  </button>
                </div>
                
                <div className="hot-take-stats">
                  <span>{hotTake.totalVotes || 0} total votes</span>
                  <span>{new Date(hotTake.createdAt).toLocaleDateString()}</span>
                </div>
                
                {/* Comments Section */}
                {showCommentsFor === hotTake.id && (
                  <div className="hot-take-comments-section">
                    {currentUser && (
                      <div className="comment-input-section">
                        <input
                          type="text"
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Add a comment..."
                          className="comment-input"
                          maxLength={280}
                        />
                        <button 
                          onClick={() => handleAddComment(hotTake.id)}
                          className="comment-submit-button"
                          disabled={!newComment.trim()}
                        >
                          Post
                        </button>
                      </div>
                    )}
                    
                    <div className="comments-list">
                      {hotTakeComments[hotTake.id]?.length > 0 ? (
                        hotTakeComments[hotTake.id].map(comment => (
                          <div key={comment.id} className="hot-take-comment">
                            <div className="comment-author">{comment.authorName}</div>
                            <div className="comment-text">{comment.text}</div>
                            <div className="comment-date">
                              {new Date(comment.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="no-comments">No comments yet</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-activity">
            {currentUser && currentUser.uid === userId ? 
              'Create your first hot take!' : 
              'No hot takes yet'
            }
          </div>
        )}
      </div>
      
      {/* Recent Comments */}
      <div className="profile-activity-section">
        <h2 className="activity-title">Recent Comments</h2>
        
        {loadingComments ? (
          <div className="loading-comments">Loading comments...</div>
        ) : userComments.length > 0 ? (
          <div className="profile-comments-grid">
            {userComments.map(comment => (
              <div key={comment.id} className="profile-comment-preview">
                <div className="comment-preview-text">{comment.text}</div>
                <div className="comment-preview-stats">
                  <span>👏 {comment.claps || 0}</span>
                  <span>🍅 {comment.tomatoes || 0}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-activity">No comments yet</div>
        )}
      </div>
      
      {/* Create Hot Take Modal */}
      {showCreateHotTake && (
        <div className="create-hot-take-modal">
          <div className="create-hot-take-content">
            <h2>Create Hot Take</h2>
            <form className="hot-take-form" onSubmit={handleCreateHotTake}>
              <div className="form-group">
                <label>Question</label>
                <textarea
                  value={newHotTake.question}
                  onChange={(e) => setNewHotTake({...newHotTake, question: e.target.value})}
                  placeholder="What's your hot take question?"
                  maxLength={200}
                  rows={3}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Option A (Yes/Agree)</label>
                <input
                  type="text"
                  value={newHotTake.optionA}
                  onChange={(e) => setNewHotTake({...newHotTake, optionA: e.target.value})}
                  placeholder="Yes/Agree option"
                  maxLength={100}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Option B (No/Disagree)</label>
                <input
                  type="text"
                  value={newHotTake.optionB}
                  onChange={(e) => setNewHotTake({...newHotTake, optionB: e.target.value})}
                  placeholder="No/Disagree option"
                  maxLength={100}
                  required
                />
              </div>
              
              <div className="form-buttons">
                <button type="button" onClick={() => setShowCreateHotTake(false)} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Create Hot Take
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Profile Modal */}
      {isEditing && (
        <div className="edit-profile-modal">
          <div className="edit-profile-content">
            <h2>Edit Profile</h2>
            <form className="profile-edit-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Display Name</label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  placeholder="Display Name"
                  maxLength={50}
                />
              </div>
              
              <div className="form-group">
                <label>Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="username"
                  maxLength={15}
                />
                {usernameError && <div className="error-message">{usernameError}</div>}
                <div className="input-help-text">
                  Will be used for @mentions. Letters, numbers, and underscores only.
                </div>
              </div>
              
              <div className="form-group">
                <label>Bio</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  placeholder="Tell us about yourself"
                  maxLength={150}
                  rows={3}
                />
              </div>
              
              <div className="form-buttons">
                <button type="button" onClick={() => setIsEditing(false)} className="cancel-button">
                  Cancel
                </button>
                <button type="submit" className="save-button">
                  Save Profile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Badge Collection Modal */}
      <BadgeModal 
        isOpen={showBadgeModal}
        onClose={() => setShowBadgeModal(false)}
        userId={userId}
      />
      
      {/* Ring Selection Modal */}
      <RingSelectionModal 
        isOpen={showRingModal}
        onClose={() => setShowRingModal(false)}
        userId={userId}
      />
    </div>
  );
}

export default Profile;